package features.members.ui

import kotlinx.browser.document
import kotlinx.html.*
import kotlinx.html.js.onClickFunction
import features.teams.domain.Team
import features.members.handlers.*
import features.members.handlers.showAddMemberModal
import features.members.handlers.openEditMember
import features.members.handlers.openMemberSchedule
import shared.ui.ModalManager

object MembersTab {
    fun render(parent: DIV, team: Team) {
        console.log("🎨 renderMembersTab called for team:", team.id)
        val allMembers = AppState.members[team.id] ?: emptyList()
        console.log("📋 All members from AppState:", allMembers.size)
        console.log("📋 Members data:", allMembers)
        val filteredMembers = filterAndSortMembers(allMembers)
        console.log("🔍 Filtered members:", filteredMembers.size)
        val selectedCount = AppState.selectedMemberIds.size
        val isArchived = team.isArchived
        
        parent.div {
            // Search and Filter Bar
            div("flex flex-wrap gap-3 mb-4") {
                // Search input
                div("flex-1 min-w-[200px]") {
                    input(classes = "w-full bg-dark-900 border border-dark-700 rounded-xl p-3 focus:outline-none focus:border-brand-cyan text-white placeholder-slate-500") {
                        id = "member-search-input"
                        placeholder = "🔍 Search members..."
                        value = AppState.memberSearchQuery
                        if (isArchived) disabled = true
                    }
                }
                
                // Role filter
                div {
                    select("bg-dark-900 border border-dark-700 rounded-xl p-3 focus:outline-none focus:border-brand-cyan text-white") {
                        id = "member-role-filter"
                        if (isArchived) disabled = true
                        
                        option { 
                            value = "All"
                            selected = AppState.memberFilterRole == "All"
                            +"All Roles" 
                        }
                        option { 
                            value = "Owner"
                            selected = AppState.memberFilterRole == "Owner"
                            +"Owner" 
                        }
                        option { 
                            value = "Admin"
                            selected = AppState.memberFilterRole == "Admin"
                            +"Admin" 
                        }
                        option { 
                            value = "Member"
                            selected = AppState.memberFilterRole == "Member"
                            +"Member" 
                        }
                    }
                }
                
                // Sort dropdown
                div {
                    select("bg-dark-900 border border-dark-700 rounded-xl p-3 focus:outline-none focus:border-brand-cyan text-white") {
                        id = "member-sort-by"
                        if (isArchived) disabled = true
                        
                        option { 
                            value = "name-asc"
                            selected = AppState.memberSortBy == "name-asc"
                            +"Name A-Z" 
                        }
                        option { 
                            value = "name-desc"
                            selected = AppState.memberSortBy == "name-desc"
                            +"Name Z-A" 
                        }
                        option { 
                            value = "role"
                            selected = AppState.memberSortBy == "role"
                            +"Role" 
                        }
                        option { 
                            value = "joinDate"
                            selected = AppState.memberSortBy == "joinDate"
                            +"Join Date" 
                        }
                    }
                }
            }
            
            // Action Buttons Row
            div("flex justify-between items-center mb-4") {
                h2("text-2xl font-bold") { +"Team Members (${filteredMembers.size})" }
                
                // Only show member management buttons for owners
                if (team.role == "owner") {
                    div("flex gap-2") {
                        button(classes = "bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg font-medium transition flex items-center gap-2") {
                            onClickFunction = { 
                                ModalManager.show("import-export-modal")
                            }
                            if (isArchived) disabled = true
                            span("text-sm") { +"📊" }
                            +"Import/Export"
                        }
                        button(classes = "bg-brand-cyan hover:bg-brand-green text-white px-4 py-2 rounded-lg font-medium transition flex items-center gap-2") {
                            onClickFunction = { showAddMemberModal() }
                            if (isArchived) disabled = true
                            span("text-xl") { +"+" }
                            +"Add Member"
                        }
                    }
                }
            }
            
            // Bulk Action Toolbar (visible when members selected and user is owner)
            if (selectedCount > 0 && !isArchived && team.role == "owner") {
                div("bg-brand-cyan/10 border border-brand-cyan/30 rounded-xl p-4 mb-4 flex justify-between items-center") {
                    span("text-brand-cyan font-bold") { +"$selectedCount member${if (selectedCount > 1) "s" else ""} selected" }
                    div("flex gap-2") {
                        button(classes = "px-4 py-2 rounded-lg bg-dark-700 hover:bg-dark-600 text-white transition text-sm") {
                            onClickFunction = { showBulkEditRoleModal() }
                            +"✏️ Edit Role"
                        }
                        button(classes = "px-4 py-2 rounded-lg bg-dark-700 hover:bg-dark-600 text-white transition text-sm") {
                            onClickFunction = { 
                                val selectedMembers = allMembers.filter { it.id in AppState.selectedMemberIds }
                                exportMembersToCSV(selectedMembers)
                            }
                            +"📥 Export"
                        }
                        button(classes = "px-4 py-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30 transition text-sm") {
                            onClickFunction = { showBulkDeleteModal() }
                            +"🗑️ Delete"
                        }
                        button(classes = "px-3 py-2 rounded-lg bg-dark-800 hover:bg-dark-700 text-slate-400 transition text-sm") {
                            onClickFunction = { 
                                AppState.selectedMemberIds.clear()
                                Router.render()
                            }
                            +"✕"
                        }
                    }
                }
            }
            
            // Members List
            if (filteredMembers.isEmpty()) {
                div("text-center py-12 text-slate-500") {
                    img(src = "/images/empty-states/no-teams.png", alt = "No members") {
                        classes = setOf("w-48", "h-48", "mx-auto", "mb-4")
                    }
                    if (allMembers.isEmpty()) {
                        +"No members yet"
                    } else {
                        +"No members match your filters"
                    }
                }
            } else {
                // Select All Checkbox (only for owners)
                if (!isArchived && team.role == "owner") {
                    div("bg-dark-800 p-3 rounded-t-xl border-b border-dark-700 flex items-center gap-3") {
                        input(type = InputType.checkBox, classes = "w-4 h-4 rounded border-dark-600 bg-dark-900 text-brand-cyan focus:ring-brand-cyan") {
                            checked = selectedCount == allMembers.size && allMembers.isNotEmpty()
                            onClickFunction = { toggleSelectAllMembers() }
                        }
                        span("text-sm text-slate-400") { +"Select All" }
                    }
                }
                
                // Member Cards
                filteredMembers.forEach { member ->
                    div("bg-dark-800 p-4 border-b border-dark-700 last:border-0 hover:bg-dark-700/50 transition flex items-center gap-4") {
                        // Checkbox (only for owners)
                        if (!isArchived && team.role == "owner") {
                            input(type = InputType.checkBox, classes = "w-4 h-4 rounded border-dark-600 bg-dark-900 text-brand-cyan focus:ring-brand-cyan") {
                                checked = member.id in AppState.selectedMemberIds
                                onClickFunction = {
                                    toggleMemberSelection(member.id)
                                }
                            }
                        }
                        div("flex-1 flex items-center gap-3") {
                            img(src = "https://ui-avatars.com/api/?name=${member.name}&background=6366f1&color=fff") {
                                classes = setOf("w-10", "h-10", "rounded-full")
                            }
                            div {
                                div("font-bold text-white") { +member.name }
                                div("text-sm text-slate-400") { +member.email }
                            }
                        }
                        div("flex items-center gap-3") {
                            span("px-3 py-1 rounded-full bg-brand-cyan/20 text-brand-cyan text-sm font-medium") {
                                +member.role
                            }
                            if (!isArchived && team.role == "owner") {
                                // Don't show edit buttons for owner member
                                if (member.role != "owner") {
                                    div("flex gap-2") {
                                        button(classes = "px-3 py-1 rounded-lg bg-dark-700 hover:bg-dark-600 text-slate-300 text-sm transition") {
                                            onClickFunction = { 
                                                console.log("Edit button clicked for ${member.name}")
                                                openEditMember(member.id) 
                                            }
                                            +"Edit"
                                        }
                                        button(classes = "px-3 py-1 rounded-lg bg-dark-700 hover:bg-dark-600 text-slate-300 text-sm transition") {
                                            onClickFunction = { 
                                                console.log("Schedule button clicked for ${member.name}")
                                                openMemberSchedule(member.id) 
                                            }
                                            +"Schedule"
                                        }
                                    }
                                } else {
                                    // Show owner badge instead
                                    span("px-3 py-1 rounded-lg bg-purple-500/20 text-purple-400 text-xs font-bold border border-purple-500/30") {
                                        +"👑 Team Owner"
                                    }
                                }
                            } else if (member.role == "owner") {
                                // Show owner badge for non-owners viewing
                                span("px-3 py-1 rounded-lg bg-purple-500/20 text-purple-400 text-xs font-bold border border-purple-500/30") {
                                    +"👑 Team Owner"
                                }
                            }
                        }
                    }
                }
            }
        }
        
        // Setup event listeners for search/filter/sort
        kotlinx.browser.window.setTimeout({
            var searchTimeout: Int? = null
            
            // Search input with debounce
            val searchInput = document.getElementById("member-search-input") as? org.w3c.dom.HTMLInputElement
            searchInput?.addEventListener("input", {
                // Clear previous timeout
                searchTimeout?.let { kotlinx.browser.window.clearTimeout(it) }
                
                // Set new timeout for debounced search (300ms)
                searchTimeout = kotlinx.browser.window.setTimeout({
                    AppState.memberSearchQuery = searchInput.value
                    
                    // Save cursor position
                    val cursorPos = searchInput.selectionStart
                    
                    // Re-render
                    Router.render()
                    
                    // Restore focus and cursor position after render
                    kotlinx.browser.window.setTimeout({
                        val newSearchInput = document.getElementById("member-search-input") as? org.w3c.dom.HTMLInputElement
                        newSearchInput?.focus()
                        newSearchInput?.setSelectionRange(cursorPos ?: 0, cursorPos ?: 0)
                    }, 10)
                }, 300)
            })
            
            // Role filter
            val roleFilter = document.getElementById("member-role-filter") as? org.w3c.dom.HTMLSelectElement
            roleFilter?.addEventListener("change", {
                AppState.memberFilterRole = roleFilter.value
                Router.render()
            })
            
            // Sort by
            val sortBy = document.getElementById("member-sort-by") as? org.w3c.dom.HTMLSelectElement
            sortBy?.addEventListener("change", {
                AppState.memberSortBy = sortBy.value
                Router.render()
            })
        }, 50)
    }
}
